/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use pre-registered patient columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type patients and institutions 
 */
import {
    IInstitution
} from '@ngt/opms';

import { Chip, styled } from '@mui/material';

/*
 * Used to format datetimes
 */
import { DateTime } from 'luxon';

import { GridColDef } from '@mui/x-data-grid';



/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import { Patient, PatientStateType } from '../api/dtos';
/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));

const useRadiologistGridColDefs = (institutions: IInstitution[] | null, showInitials: boolean, showSite: boolean, showDateReviewed: boolean): GridColDef<Patient>[] => {
    const columns = React.useMemo(() => {
        const c: (GridColDef<Patient> | boolean)[] = [
            {
                field: 'studyNumber',
                headerName: 'Study Number',
                width: 140
            },
            showInitials && {
                field: 'initials',
                headerName: 'Initials'
            },
            {
                field: 'institutionCode',
                headerName: 'Site Code',
                renderCell: rowData => (
                    <StyledChip
                        label={rowData.row.institutionCode}
                        size="small"
                    />
                ),
                width: 110
            },
            {
                field: 'institutionId',
                headerName: 'Institution',
                valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
                flex: 1
            },
            {
                field: 'patientStateId',
                headerName: 'Status',
                valueGetter: (_value, row) => 
                    row.patientStateId === PatientStateType.RadiologistReview ? 'Radiologist Review' : 
                        ((row.patientStateId ?? 0) > PatientStateType.RadiologistReview && (row.patientStateId ?? 0) < PatientStateType.ScreenFail) ? 'Reviewed' : '',
                width: 200
            },
            showDateReviewed && {
                field: 'reviewDate',
                headerName: 'Date Reviewed',
                renderCell: rowData => (
                    rowData.row.reviewDate ? DateTime.fromISO(rowData.row.reviewDate).toFormat('dd/MM/yyyy') : null
                ),
                width: 150
            }
        ];

        return c.filter(column => !!column) as GridColDef<Patient>[];
    }, [showSite, institutions, showDateReviewed]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useRadiologistGridColDefs;